import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface IParamCodec<T> {
  defaultValue: T;
  decode(raw: string): T | undefined;
  encode(value: T): string | undefined;
}

type SchemaValues<S> = { [K in keyof S]: S[K] extends IParamCodec<infer T> ? T : never };

export function stringParam(defaultValue = ''): IParamCodec<string> {
  return {
    defaultValue,
    decode: (raw) => raw,
    encode: (value) => (value === defaultValue ? undefined : value),
  };
}

export function positiveIntParam(defaultValue: number): IParamCodec<number> {
  return {
    defaultValue,
    decode: (raw) => {
      const value = Number(raw);
      return Number.isInteger(value) && value > 0 ? value : undefined;
    },
    encode: (value) => (value === defaultValue ? undefined : String(value)),
  };
}

export function booleanParam(defaultValue = false): IParamCodec<boolean> {
  return {
    defaultValue,
    decode: (raw) => (raw === '1' ? true : raw === '0' ? false : undefined),
    encode: (value) => (value === defaultValue ? undefined : value ? '1' : '0'),
  };
}

export function oneOfParam<T extends string | number>(values: readonly T[], defaultValue: T): IParamCodec<T> {
  return {
    defaultValue,
    decode: (raw) => values.find((value) => String(value) === raw),
    encode: (value) => (value === defaultValue ? undefined : String(value)),
  };
}

export function stringListParam(): IParamCodec<string[]> {
  return {
    defaultValue: Object.freeze([]) as unknown as string[],
    decode: (raw) => raw.split(',').filter(Boolean),
    encode: (value) => (value.length === 0 ? undefined : value.join(',')),
  };
}

export default function useListStateParams<S extends Record<string, IParamCodec<unknown>>>(
  schema: S,
): { state: SchemaValues<S>; update: (patch: Partial<SchemaValues<S>>) => void } {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => {
    const values: Record<string, unknown> = {};
    for (const [key, codec] of Object.entries(schema)) {
      const raw = searchParams.get(key);
      const decoded = raw === null ? undefined : codec.decode(raw);
      values[key] = decoded === undefined ? codec.defaultValue : decoded;
    }
    return values as SchemaValues<S>;
  }, [searchParams, schema]);

  const update = useCallback(
    (patch: Partial<SchemaValues<S>>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(patch)) {
            if (value === undefined) continue;
            const encoded = schema[key].encode(value);
            if (encoded === undefined) {
              next.delete(key);
            } else {
              next.set(key, encoded);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [schema, setSearchParams],
  );

  return { state, update };
}
